import { useState } from 'react'
import { motion } from 'framer-motion'
import { Tv2, Users, Wifi, WifiOff, Settings, Play, RefreshCw, MapPin, CheckCircle, Clock, AlertTriangle } from 'lucide-react'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { EmptyState } from '../ui/empty-state'

type RoomStatus = 'available' | 'in-use' | 'offline'

interface BoardRoomDevice {
  id: string
  name: string
  location: string
  capacity: number
  status: RoomStatus
  ipAddress: string
  firmware: string
  lastSeen: string
  currentMeeting?: string
}

const ROOMS: BoardRoomDevice[] = [
  { id: 'r1', name: 'Boardroom Alpha', location: 'Floor 3 – East Wing', capacity: 12, status: 'available', ipAddress: '192.168.1.101', firmware: 'v3.2.1', lastSeen: '2 min ago' },
  { id: 'r3', name: 'Innovation Lab', location: 'Floor 2 – West', capacity: 6, status: 'available', ipAddress: '192.168.1.103', firmware: 'v3.1.8', lastSeen: '5 min ago' },
  { id: 'r4', name: 'Training Room A', location: 'Floor 1 – South', capacity: 20, status: 'offline', ipAddress: '192.168.1.104', firmware: 'v3.0.4', lastSeen: '2 hours ago' },
  { id: 'r5', name: 'Huddle Space B', location: 'Floor 4 – Central', capacity: 4, status: 'in-use', ipAddress: '192.168.1.105', firmware: 'v3.2.1', lastSeen: 'Just now', currentMeeting: 'Design Sprint' },
  { id: 'r2', name: 'Executive Suite', location: 'Floor 5 – North', capacity: 8, status: 'in-use', ipAddress: '192.168.1.102', firmware: 'v3.2.1', lastSeen: 'Just now', currentMeeting: 'Q3 Board Review' },

]

const statusConfig: Record<RoomStatus, { label: string; icon: React.ReactNode; badge: string }> = {
  available: {
    label: 'Available',
    icon: <CheckCircle className="w-3.5 h-3.5 text-green-500" />,
    badge: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  },
  'in-use': {
    label: 'In Use',
    icon: <Play className="w-3.5 h-3.5 text-blue-500" />,
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  },
  offline: {
    label: 'Offline',
    icon: <WifiOff className="w-3.5 h-3.5 text-muted-foreground" />,
    badge: 'bg-muted text-muted-foreground',
  },
}

export default function BoardRoom() {
  const [filter, setFilter] = useState<'all' | RoomStatus>('all')
  const [refreshing, setRefreshing] = useState(false)

  const filtered = ROOMS.filter(r => filter === 'all' || r.status === filter)

  const stats = {
    total: ROOMS.length,
    available: ROOMS.filter(r => r.status === 'available').length,
    inUse: ROOMS.filter(r => r.status === 'in-use').length,
    offline: ROOMS.filter(r => r.status === 'offline').length,
  }

  const handleRefresh = () => {
    setRefreshing(true)
    setTimeout(() => setRefreshing(false), 1200)
  }

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      {/* Top bar */}
      <div className="px-5 py-4 border-b border-stroke-100 dark:border-border shrink-0 flex items-center justify-between">
        <div>
          <h2 className="text-base text-sm font-bold text-foreground">Board Rooms</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Connected conference hardware devices</p>
        </div>
        <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={handleRefresh}>
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="px-5 py-3 grid grid-cols-2 sm:grid-cols-4 gap-3 shrink-0">
        {[
          { 
            label: 'Total', 
            value: stats.total, 
            icon: <Tv2 className="w-4 h-4 text-slate-600 dark:text-slate-400" />, 
            bg: 'bg-gradient-to-br from-slate-50 to-gray-100 dark:from-slate-900/20 dark:to-gray-900/20' 
          },
          { 
            label: 'Available', 
            value: stats.available, 
            icon: <CheckCircle className="w-4 h-4 text-green-500" />, 
            bg: 'bg-gradient-to-br from-emerald-50 to-teal-100 dark:from-emerald-900/20 dark:to-teal-900/20' 
          },
          { 
            label: 'In Use', 
            value: stats.inUse, 
            icon: <Play className="w-4 h-4 text-blue-500" />, 
            bg: 'bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20' 
          },
          { 
            label: 'Offline', 
            value: stats.offline, 
            icon: <WifiOff className="w-4 h-4 text-slate-500 dark:text-slate-400" />, 
            bg: 'bg-gradient-to-br from-red-50 to-pink-100 dark:from-red-900/20 dark:to-pink-900/20' 
          },
        ].map(s => (
          <div key={s.label} className="relative bg-card dark:bg-card border border-border rounded-2xl p-3 shadow-xs hover:shadow-md transition-all duration-200 overflow-hidden">
            {/* Decorative Background Wave */}
            <div className={`absolute inset-0 ${s.bg}`}>
              <svg 
                className="absolute bottom-0 right-0 w-full h-full" 
                viewBox="0 0 200 120" 
                fill="none" 
                preserveAspectRatio="none"
              >
                <path 
                  d="M200 120V80C180 70 160 75 140 80C120 85 100 90 80 85C60 80 40 70 20 75C10 77.5 5 79 0 80V120H200Z" 
                  fill="currentColor" 
                  className="text-white/30 dark:text-white/10"
                />
              </svg>
            </div>

            {/* Content */}
            <div className="relative z-10 flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-card/50 dark:bg-card/10 flex items-center justify-center shrink-0">
                {s.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xl font-bold text-foreground leading-none">{s.value}</div>
                <div className="text-xs font-medium text-muted-foreground mt-0.5">{s.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filter chips */}
      <div className="px-5 py-3 flex items-center gap-2 shrink-0">
        {(['all', 'available', 'in-use', 'offline'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all ${
              filter === f
                ? 'bg-primary text-white shadow-sm'
                : 'bg-muted text-muted-foreground hover:bg-muted/70'
            }`}
          >
            {f === 'all' ? 'All' : f === 'in-use' ? 'In Use' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Room cards */}
      <div className="flex-1 overflow-y-auto px-5 pb-5">
        {filtered.length === 0 ? (
          <EmptyState icon={Tv2} title="No rooms found" description="No devices match the selected filter." />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {filtered.map((room, i) => {
              const sc = statusConfig[room.status]
              return (
                <motion.div
                  key={room.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="bg-card dark:bg-card shadow-sm border border-stroke-100 dark:border-border rounded-2xl hover:shadow-lg transition-all group overflow-hidden relative flex flex-col h-full"
                >
                  <div className="flex flex-col h-full p-5">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          room.status === 'available' ? 'bg-emerald-100 dark:bg-emerald-900/30' :
                          room.status === 'in-use' ? 'bg-blue-100 dark:bg-blue-900/30' :
                          'bg-slate-100 dark:bg-slate-800'
                        }`}>
                          <Tv2 className={`w-5 h-5 ${
                            room.status === 'available' ? 'text-emerald-600 dark:text-emerald-400' :
                            room.status === 'in-use' ? 'text-blue-600 dark:text-blue-400' :
                            'text-slate-500 dark:text-slate-400'
                          }`} />
                        </div>
                        <div>
                          <h3 className="text-base font-semibold text-foreground">{room.name}</h3>
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                            <MapPin className="w-3 h-3" />{room.location}
                          </p>
                        </div>
                      </div>
                      <Badge variant="secondary" className={`text-xs flex items-center gap-1 px-2 py-1 ${sc.badge}`}>
                        {sc.icon}{sc.label}
                      </Badge>
                    </div>

                    {/* Current Meeting */}
                    {room.currentMeeting && (
                      <div className="mb-4 px-3 py-2.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                        <p className="text-sm font-medium text-blue-700 dark:text-blue-300 flex items-center gap-1.5">
                          <Play className="w-3.5 h-3.5" /> {room.currentMeeting}
                        </p>
                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">Meeting in progress</p>
                      </div>
                    )}

                    {/* Room Details */}
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center justify-between py-2 border-b border-border/50">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">Capacity</span>
                        </div>
                        <span className="text-sm font-medium text-foreground">{room.capacity} people</span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-border/50">
                        <div className="flex items-center gap-2">
                          <Wifi className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">Network</span>
                        </div>
                        <span className="text-xs font-mono text-foreground">{room.ipAddress}</span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-border/50">
                        <div className="flex items-center gap-2">
                          <RefreshCw className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">Firmware</span>
                        </div>
                        <span className="text-xs font-medium text-foreground">{room.firmware}</span>
                      </div>
                      <div className="flex items-center justify-between py-2">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">Last Seen</span>
                        </div>
                        <span className="text-xs font-medium text-foreground">{room.lastSeen}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="mt-auto flex gap-2 pt-3 border-t border-border/50">
                      <Button size="sm" variant="outline" className="flex-1 text-xs gap-1.5">
                        <Settings className="w-3.5 h-3.5" />Configure
                      </Button>
                      {room.status === 'available' && (
                        <Button size="sm" className="flex-1 text-xs gap-1.5">
                          <Play className="w-3.5 h-3.5" />Launch
                        </Button>
                      )}
                      {room.status === 'offline' && (
                        <Button size="sm" variant="destructive" className="flex-1 text-xs gap-1.5">
                          <AlertTriangle className="w-3.5 h-3.5" />Diagnose
                        </Button>
                      )}
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
